/**
 * Each clip's colour (Dax, 2026-10-04): clips take hues spread evenly round the wheel in timeline order,
 * starting from --clip-hue-start, so four clips are four clearly different tints and twenty-four still
 * step from one to the next. A clip's waveform, its darker accent and its very pale background share its
 * hue, on the composer's card and on the audio desk's strips alike, so clip 2 is the same colour wherever
 * it appears. Saturation and lightness are tokens; only the hue is computed.
 */

/** The hue (degrees) of clip `index` of `count`, from `startDeg`. */
export function clipHue(index: number, count: number, startDeg: number): number {
  return (((startDeg + (index * 360) / Math.max(1, count)) % 360) + 360) % 360;
}

export interface ClipColours { wave: string; accent: string; bg: string }

function token(style: CSSStyleDeclaration, name: string, fallback: number): number {
  const v = parseFloat(style.getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
}

/** Clip `index` of `count` as colours, read against the page's tokens. */
export function clipColours(index: number, count: number): ClipColours {
  const s = getComputedStyle(document.documentElement);
  const h = clipHue(index, count, token(s, '--clip-hue-start', 28));
  return {
    wave: `hsl(${h} ${token(s, '--clip-wave-sat', 42)}% ${token(s, '--clip-wave-light', 44)}%)`,
    accent: `hsl(${h} ${token(s, '--clip-accent-sat', 50)}% ${token(s, '--clip-accent-light', 34)}%)`,
    bg: `hsl(${h} ${token(s, '--clip-bg-sat', 55)}% ${token(s, '--clip-bg-light', 95)}%)`,
  };
}
